import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft,
  Compass,
  Star,
  Sun,
  Moon,
  Sunrise,
  Sparkles,
  Heart,
  Briefcase,
  Target,
  AlertTriangle,
  Crown,
  MapPin,
  Clock,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const BirthChartPage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [birthTime, setBirthTime] = useState(user?.birth_time || "");
  const [birthPlace, setBirthPlace] = useState(user?.birth_place || "");
  const [showUpdateForm, setShowUpdateForm] = useState(false);

  const fetchBirthChart = async () => {
    try {
      const response = await api.get("/premium/birth-chart");
      setReading(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Premium subscription required");
        navigate("/premium");
      } else if (error.response?.status === 400) {
        toast.error("Please complete your profile with date of birth");
        navigate("/profile");
      } else {
        console.error("Error fetching birth chart:", error);
        toast.error("Failed to load birth chart");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBirthChart();
  }, []);

  const updateBirthDetails = async () => {
    setGenerating(true);
    try {
      await api.post("/premium/birth-chart", {
        birth_time: birthTime,
        birth_place: birthPlace,
      });
      
      // Refresh user data
      const userResponse = await api.get("/auth/me");
      setUser(userResponse.data);
      
      // Fetch new reading
      const response = await api.get("/premium/birth-chart");
      setReading(response.data);
      setShowUpdateForm(false);
      toast.success("Birth chart updated!");
    } catch (error) {
      console.error("Error updating birth details:", error);
      toast.error("Failed to update birth details");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-white/70 font-body">Reading the stars...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="birth-chart-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#070B14]/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/premium")}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
                <Star className="w-4 h-4 text-[#070B14]" />
              </div>
              <span className="font-display text-xl text-white">Birth Chart</span>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
            <Crown className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs text-[#D4AF37] font-body">Premium</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {reading && (
          <>
            {/* Main Signs */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4 animate-fade-in-up">
              <div className="gold-accent-card p-4 sm:p-6 text-center">
                <Sun className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
                <p className="text-xs text-white/50 font-body uppercase tracking-wider">Sun Sign</p>
                <p className="font-display text-xl sm:text-2xl text-white">{reading.sun_sign}</p>
              </div>
              <div className="glass-card p-4 sm:p-6 text-center">
                <Moon className="w-8 h-8 text-[#C4B5FD] mx-auto mb-2" />
                <p className="text-xs text-white/50 font-body uppercase tracking-wider">Moon Sign</p>
                <p className="font-display text-xl sm:text-2xl text-white">{reading.moon_sign}</p>
              </div>
              <div className="glass-card p-4 sm:p-6 text-center">
                <Sunrise className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                <p className="text-xs text-white/50 font-body uppercase tracking-wider">Rising</p>
                <p className="font-display text-xl sm:text-2xl text-white">{reading.rising_sign}</p>
              </div>
            </div>

            {/* Life Path Number */}
            <div className="gold-accent-card p-6 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                  <span className="font-display text-4xl text-[#D4AF37]">{reading.life_path_number}</span>
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-xs text-[#D4AF37] font-body uppercase tracking-wider mb-1">Life Path Number</p>
                  <h3 className="font-display text-xl text-white mb-2">{reading.life_path_meaning}</h3>
                </div>
              </div>
            </div>

            {/* Birth Chart Summary */}
            <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-5 h-5 text-[#C4B5FD]" />
                <h3 className="font-display text-xl text-white">Your Cosmic Blueprint</h3>
              </div>
              <p className="text-white/80 font-body leading-relaxed whitespace-pre-line">{reading.birth_chart_summary}</p>
            </div>

            {/* Personality Traits */}
            <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
              <h3 className="font-display text-xl text-white mb-4">Personality Traits</h3>
              <div className="flex flex-wrap gap-2">
                {reading.personality_traits.map((trait, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 rounded-full bg-[#8B5CF6]/20 border border-[#8B5CF6]/30 text-[#C4B5FD] text-sm font-body"
                  >
                    {trait}
                  </span>
                ))}
              </div>
            </div>

            {/* Strengths & Challenges */}
            <div className="grid md:grid-cols-2 gap-4 animate-fade-in-up" style={{ animationDelay: "0.25s" }}>
              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="font-display text-lg text-white">Strengths</h3>
                </div>
                <ul className="space-y-2">
                  {reading.strengths.map((strength, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-white/80 font-body text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-2 flex-shrink-0" />
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-orange-400" />
                  </div>
                  <h3 className="font-display text-lg text-white">Challenges</h3>
                </div>
                <ul className="space-y-2">
                  {reading.challenges.map((challenge, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-white/80 font-body text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2 flex-shrink-0" />
                      {challenge}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Love & Career */}
            <div className="grid md:grid-cols-2 gap-4 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-pink-400" />
                  </div>
                  <h3 className="font-display text-lg text-white">Love & Relationships</h3>
                </div>
                <p className="text-white/70 font-body text-sm leading-relaxed">{reading.love_compatibility}</p>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 className="font-display text-lg text-white">Career Path</h3>
                </div>
                <p className="text-white/70 font-body text-sm leading-relaxed">{reading.career_guidance}</p>
              </div>
            </div>

            {/* Spiritual Path */}
            <div className="gold-accent-card p-6 animate-fade-in-up" style={{ animationDelay: "0.35s" }}>
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                <h3 className="font-display text-xl text-white">Spiritual Path</h3>
              </div>
              <p className="text-white/80 font-body leading-relaxed">{reading.spiritual_path}</p>
            </div>

            {/* Update Birth Details */}
            <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg text-white">Enhance Your Reading</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowUpdateForm(!showUpdateForm)}
                  className="text-[#D4AF37] hover:bg-[#D4AF37]/10"
                >
                  {showUpdateForm ? "Cancel" : "Add Details"}
                </Button>
              </div>
              
              {showUpdateForm ? (
                <div className="space-y-4">
                  <p className="text-white/60 text-sm font-body">
                    Add your birth time and place for a more accurate reading
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-white/70 font-body flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Birth Time
                      </label>
                      <Input
                        type="time"
                        value={birthTime}
                        onChange={(e) => setBirthTime(e.target.value)}
                        className="custom-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-white/70 font-body flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> Birth Place
                      </label>
                      <Input
                        type="text"
                        placeholder="City, Country"
                        value={birthPlace}
                        onChange={(e) => setBirthPlace(e.target.value)}
                        className="custom-input"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={updateBirthDetails}
                    disabled={generating}
                    className="btn-gold w-full sm:w-auto"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Regenerating...
                      </>
                    ) : (
                      "Update & Regenerate"
                    )}
                  </Button>
                </div>
              ) : (
                <p className="text-white/50 text-sm font-body">
                  Add your exact birth time and location for more precise moon sign and rising sign calculations
                </p>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default BirthChartPage;
